export interface DashboardData {
  models: number;
  sources: number;
  routes: number;
  pendingOpportunities: number;
  freeSources: number;
  paidSources: number;
  globalPaidBudgetUsd: number;
}

export interface ModelRow {
  id: string;
  display_name: string;
  context_window: number;
  max_output_tokens: number;
  capabilities_json: string;
  enabled: number;
  route_count?: number;
  open_route_count?: number;
  observed_route_count?: number;
  operational_status?: "healthy" | "degraded" | "circuit_open" | "half_open" | "unverified" | "unconfigured" | "disabled";
}

export interface SourceHealth {
  circuitState: "closed" | "half-open" | "open";
  consecutiveFailures: number;
  lastError: string | null;
  successRate: number;
  latencyEwmaMs: number;
  remainingRatio: number | null;
  anomaly?: "circuit_open" | "quota_exhausted" | "auth_error" | null;
}

export interface SourceRow {
  id: string;
  name: string;
  preset: string;
  protocol: "chat" | "responses";
  base_url: string;
  tier: "free" | "paid";
  endpoint_country: string;
  enabled: number;
  geo_policy_id: string;
  health?: SourceHealth;
}

export interface RouteScoreBreakdown {
  priorityWeight: number; // 45%
  successWeight: number;  // 30%
  quotaWeight: number;    // 15%
  latencyWeight: number;  // 10%
  totalScore: number;
}

export interface RouteRow {
  id: string;
  logical_model_id: string;
  source_id: string;
  upstream_model_id: string;
  manual_priority: number;
  source_name: string;
  tier: string;
  endpoint_country: string;
  success_rate?: number | null;
  latency_ms?: number | null;
  consecutive_failures?: number | null;
  circuit_open_until?: number | null;
  half_open_probe_until?: number | null;
  last_error_code?: string | null;
  health_updated_at?: number | null;
  scoreBreakdown?: RouteScoreBreakdown;
  geoBlocked?: boolean;
  geoBlockedReason?: string;
}

export interface GeoPolicyRow {
  id: string;
  name: string;
  restricted: number;
  allowed_request_countries_json: string;
  allowed_endpoint_countries_json: string;
  policy_url: string | null;
  verified_at: number;
  valid_days: number;
  last_verified_by?: string;
}

export interface OpportunityRow {
  id: string;
  provider: string;
  title: string;
  url: string;
  description: string;
  opportunity_type: string;
  status: "pending" | "approved" | "dismissed";
  discovered_at: number;
  probeResult?: { reachable: boolean; protocolMatch: boolean; latencyMs: number };
  assignedModelId?: string;
}

export interface KeyRow {
  id: string;
  name: string;
  key_prefix: string;
  allowed_models_json: string;
  requests_per_minute: number;
  max_concurrency: number;
  enabled: number;
  last_used_at: number | null;
  created_at: number;
}

export interface CountryPriorityRow {
  id: string;
  name: string;
  groups_json: string;
  allow_unknown: number;
}

export interface AuditRow {
  id: string;
  actor: string;
  action: string;
  entity_type: string;
  entity_id: string;
  summary_json: string;
  created_at: number;
}

export interface RequestDecisionStep {
  stage: string;
  detail: string;
  status: "pass" | "excluded" | "failed" | "selected";
}

export interface RequestLogItem {
  id: string;
  timestamp: number;
  logicalModel: string;
  requestCountry: string;
  finalSource: string | null;
  status: number;
  latencyMs: number;
  errorCode: string | null;
  switchableError: boolean;
  errorExplanation?: string;
  decisionChain: RequestDecisionStep[];
}

export interface BudgetStatus {
  globalLimitUsd: number;
  settledUsd: number;
  reservedUsd: number;
  remainingUsd: number;
  trend: Array<{ label: string; settled: number; reserved: number }>;
}

export interface AdminData {
  dashboard: DashboardData;
  models: ModelRow[];
  sources: SourceRow[];
  routes: RouteRow[];
  geoPolicies: GeoPolicyRow[];
  opportunities: OpportunityRow[];
  keys: KeyRow[];
  priority: CountryPriorityRow;
  audits: AuditRow[];
  budget: BudgetStatus;
  recentLogs: RequestLogItem[];
  demo: boolean;
}

export const ISO_COUNTRIES = [
  { code: "JP", name: "日本 (Japan)" },
  { code: "SG", name: "新加坡 (Singapore)" },
  { code: "US", name: "美国 (United States)" },
  { code: "DE", name: "德国 (Germany)" },
  { code: "FR", name: "法国 (France)" },
  { code: "GB", name: "英国 (United Kingdom)" },
  { code: "KR", name: "韩国 (South Korea)" },
  { code: "AU", name: "澳大利亚 (Australia)" },
  { code: "CA", name: "加拿大 (Canada)" },
  { code: "NL", name: "荷兰 (Netherlands)" },
  { code: "SE", name: "瑞典 (Sweden)" },
  { code: "HK", name: "中国香港 (Hong Kong)" },
  { code: "TW", name: "中国台湾 (Taiwan)" },
  { code: "IN", name: "印度 (India)" },
  { code: "GLOBAL", name: "全球公共 (Global)" },
];

const demoData: AdminData = {
  dashboard: {
    models: 3,
    sources: 5,
    routes: 6,
    pendingOpportunities: 2,
    freeSources: 4,
    paidSources: 1,
    globalPaidBudgetUsd: 10,
  },
  models: [
    {
      id: "deepseek-coder",
      display_name: "DeepSeek Coder",
      context_window: 128000,
      max_output_tokens: 8192,
      capabilities_json: '{"text":true,"streaming":true,"tools":true,"json":true}',
      enabled: 1,
    },
    {
      id: "kimi-code",
      display_name: "Kimi Code",
      context_window: 256000,
      max_output_tokens: 16384,
      capabilities_json: '{"text":true,"streaming":true,"tools":true,"json":true}',
      enabled: 1,
    },
    {
      id: "gemini-flash",
      display_name: "Gemini Flash",
      context_window: 1048576,
      max_output_tokens: 8192,
      capabilities_json: '{"text":true,"streaming":true,"tools":true,"json":true}',
      enabled: 1,
    },
  ],
  sources: [
    {
      id: "nvidia-free",
      name: "NVIDIA NIM Free",
      preset: "nvidia",
      protocol: "chat",
      base_url: "https://integrate.api.nvidia.com/v1",
      tier: "free",
      endpoint_country: "US",
      enabled: 1,
      geo_policy_id: "nvidia",
      health: {
        circuitState: "closed",
        consecutiveFailures: 0,
        lastError: null,
        successRate: 0.99,
        latencyEwmaMs: 145,
        remainingRatio: 0.82,
      },
    },
    {
      id: "openrouter-free",
      name: "OpenRouter Free Pool",
      preset: "openrouter",
      protocol: "chat",
      base_url: "https://openrouter.ai/api/v1",
      tier: "free",
      endpoint_country: "US",
      enabled: 1,
      geo_policy_id: "openrouter",
      health: {
        circuitState: "half-open",
        consecutiveFailures: 1,
        lastError: "rate_limit_exceeded (429)",
        successRate: 0.88,
        latencyEwmaMs: 260,
        remainingRatio: 0.35,
        anomaly: "circuit_open",
      },
    },
    {
      id: "gemini-free",
      name: "Gemini Free Tier",
      preset: "google",
      protocol: "chat",
      base_url: "https://generativelanguage.googleapis.com/v1beta/openai",
      tier: "free",
      endpoint_country: "SG",
      enabled: 1,
      geo_policy_id: "google",
      health: {
        circuitState: "closed",
        consecutiveFailures: 0,
        lastError: null,
        successRate: 0.97,
        latencyEwmaMs: 110,
        remainingRatio: 0.9,
      },
    },
    {
      id: "deepseek-paid",
      name: "DeepSeek Official Paid",
      preset: "deepseek",
      protocol: "chat",
      base_url: "https://api.deepseek.com/v1",
      tier: "paid",
      endpoint_country: "SG",
      enabled: 1,
      geo_policy_id: "deepseek",
      health: {
        circuitState: "closed",
        consecutiveFailures: 0,
        lastError: null,
        successRate: 0.995,
        latencyEwmaMs: 95,
        remainingRatio: 0.74,
      },
    },
    {
      id: "moonshot-free",
      name: "Moonshot Kimi Free",
      preset: "moonshot",
      protocol: "chat",
      base_url: "https://api.moonshot.cn/v1",
      tier: "free",
      endpoint_country: "JP",
      enabled: 1,
      geo_policy_id: "unrestricted",
      health: {
        circuitState: "closed",
        consecutiveFailures: 0,
        lastError: null,
        successRate: 0.96,
        latencyEwmaMs: 160,
        remainingRatio: 0.5,
      },
    },
  ],
  routes: [
    {
      id: "r1",
      logical_model_id: "deepseek-coder",
      source_id: "nvidia-free",
      upstream_model_id: "deepseek-ai/deepseek-v4-flash",
      manual_priority: 15,
      source_name: "NVIDIA NIM Free",
      tier: "free",
      endpoint_country: "US",
      scoreBreakdown: {
        priorityWeight: 38.25,
        successWeight: 29.7,
        quotaWeight: 12.3,
        latencyWeight: 8.8,
        totalScore: 89.05,
      },
    },
    {
      id: "r2",
      logical_model_id: "deepseek-coder",
      source_id: "openrouter-free",
      upstream_model_id: "deepseek/deepseek-chat",
      manual_priority: 20,
      source_name: "OpenRouter Free Pool",
      tier: "free",
      endpoint_country: "US",
      scoreBreakdown: {
        priorityWeight: 36.0,
        successWeight: 26.4,
        quotaWeight: 5.25,
        latencyWeight: 6.5,
        totalScore: 74.15,
      },
    },
    {
      id: "r3",
      logical_model_id: "deepseek-coder",
      source_id: "deepseek-paid",
      upstream_model_id: "deepseek-chat",
      manual_priority: 90,
      source_name: "DeepSeek Official Paid",
      tier: "paid",
      endpoint_country: "SG",
      scoreBreakdown: {
        priorityWeight: 4.5,
        successWeight: 29.85,
        quotaWeight: 11.1,
        latencyWeight: 9.5,
        totalScore: 54.95,
      },
    },
  ],
  geoPolicies: [
    {
      id: "openai",
      name: "OpenAI Supported Countries",
      restricted: 1,
      allowed_request_countries_json: '["JP","SG","US","DE","FR","GB","KR","AU","CA","NL"]',
      allowed_endpoint_countries_json: '[]',
      policy_url: "https://help.openai.com/en/articles/5347006",
      verified_at: Date.now() - 86400000 * 25, // 25 days ago -> 5 days remaining (<= 7 warning!)
      valid_days: 30,
      last_verified_by: "admin@opencode.team",
    },
    {
      id: "google",
      name: "Google Gemini Available Regions",
      restricted: 1,
      allowed_request_countries_json: '["JP","SG","US","DE","FR","GB","KR","AU","CA","NL","SE"]',
      allowed_endpoint_countries_json: '[]',
      policy_url: "https://ai.google.dev/gemini-api/docs/available-regions",
      verified_at: Date.now() - 86400000 * 10,
      valid_days: 30,
      last_verified_by: "admin@opencode.team",
    },
    {
      id: "nvidia",
      name: "NVIDIA Cloud Supported Regions",
      restricted: 1,
      allowed_request_countries_json: '["JP","SG","US","DE","FR","GB","AU","CA"]',
      allowed_endpoint_countries_json: '["US"]',
      policy_url: "https://build.nvidia.com",
      verified_at: Date.now() - 86400000 * 27, // 3 days remaining!
      valid_days: 30,
      last_verified_by: "ops@opencode.team",
    },
    {
      id: "unrestricted",
      name: "No regional restriction declared",
      restricted: 0,
      allowed_request_countries_json: "[]",
      allowed_endpoint_countries_json: "[]",
      policy_url: null,
      verified_at: Date.now(),
      valid_days: 30,
      last_verified_by: "system",
    },
  ],
  opportunities: [
    {
      id: "o1",
      provider: "NVIDIA",
      title: "NVIDIA Build 目录新增免费推理端点",
      url: "https://build.nvidia.com",
      description: "发现新增兼容 Chat Completions 的公开试用模型端点，需核实使用条款。",
      opportunity_type: "api_key",
      status: "pending",
      discovered_at: Date.now() - 3600000 * 2,
      probeResult: { reachable: true, protocolMatch: true, latencyMs: 112 },
    },
    {
      id: "o2",
      provider: "AMD",
      title: "AMD Developer Cloud 算力信用申请",
      url: "https://www.amd.com/en/developer/resources/cloud-access.html",
      description: "公开发布试用算力，需人工评估是否可部署兼容的专用推理 Worker。",
      opportunity_type: "cloud_credit",
      status: "pending",
      discovered_at: Date.now() - 86400000,
      probeResult: { reachable: true, protocolMatch: false, latencyMs: 340 },
    },
  ],
  keys: [
    {
      id: "k1",
      name: "OpenCode 主工作机",
      key_prefix: "2f8a1c9d",
      allowed_models_json: "[]",
      requests_per_minute: 60,
      max_concurrency: 4,
      enabled: 1,
      last_used_at: Date.now() - 60000 * 5,
      created_at: Date.now() - 86400000 * 12,
    },
  ],
  priority: {
    id: "global",
    name: "Global default",
    groups_json: '[["JP","SG"],["US"],["DE","FR"],["GLOBAL"]]',
    allow_unknown: 0,
  },
  audits: [
    {
      id: "a1",
      actor: "admin@opencode.team",
      action: "reorder",
      entity_type: "model_route",
      entity_id: "deepseek-coder",
      summary_json: '{"reorderedCount":3}',
      created_at: Date.now() - 3600000 * 3,
    },
    {
      id: "a2",
      actor: "admin@opencode.team",
      action: "verify",
      entity_type: "geo_policy",
      entity_id: "google",
      summary_json: '{"policy":"Google Gemini Available Regions"}',
      created_at: Date.now() - 86400000 * 10,
    },
  ],
  budget: {
    globalLimitUsd: 10.0,
    settledUsd: 2.65,
    reservedUsd: 0.85,
    remainingUsd: 6.5,
    trend: [
      { label: "09-01", settled: 0.45, reserved: 0.1 },
      { label: "09-02", settled: 0.82, reserved: 0.2 },
      { label: "09-03", settled: 0.68, reserved: 0.25 },
      { label: "09-04 (今天)", settled: 0.7, reserved: 0.3 },
    ],
  },
  recentLogs: [
    {
      id: "req_9921",
      timestamp: Date.now() - 60000 * 2,
      logicalModel: "deepseek-coder",
      requestCountry: "JP",
      finalSource: "NVIDIA NIM Free",
      status: 200,
      latencyMs: 380,
      errorCode: null,
      switchableError: false,
      decisionChain: [
        { stage: "合规硬过滤", detail: "请求国 JP 在供应商 policy 允许列表，放行 3/3 个来源", status: "pass" },
        { stage: "层级决策", detail: "优先尝试免费层 (2个来源)，付费保底暂不启用", status: "pass" },
        { stage: "就近国家组", detail: "来源端点位于 US 组，进入综合打分", status: "pass" },
        { stage: "评分排序", detail: "NVIDIA NIM 得分 89.05 > OpenRouter 74.15", status: "selected" },
        { stage: "实际调用", detail: "NVIDIA NIM 正常响应 (HTTP 200)，结算完成", status: "pass" },
      ],
    },
    {
      id: "req_9918",
      timestamp: Date.now() - 60000 * 15,
      logicalModel: "deepseek-coder",
      requestCountry: "JP",
      finalSource: "NVIDIA NIM Free",
      status: 200,
      latencyMs: 720,
      errorCode: null,
      switchableError: true,
      errorExplanation: "首选来源 OpenRouter 返回 429 rate_limit_exceeded，成功触发同模型容灾切换至次选来源 NVIDIA NIM",
      decisionChain: [
        { stage: "合规硬过滤", detail: "请求国 JP 符合合规策略，放行全部候选", status: "pass" },
        { stage: "首次尝试", detail: "调用 OpenRouter Free Pool 遭遇 HTTP 429 速率超限", status: "failed" },
        { stage: "同模型切源", detail: "属于可容灾错误，触发切换至下一同模型来源 NVIDIA NIM Free", status: "selected" },
        { stage: "次选调用", detail: "NVIDIA NIM Free 响应成功，请求正常交付客户端", status: "pass" },
      ],
    },
    {
      id: "req_9915",
      timestamp: Date.now() - 60000 * 45,
      logicalModel: "deepseek-coder",
      requestCountry: "UNKNOWN",
      finalSource: null,
      status: 451,
      latencyMs: 12,
      errorCode: "geo_restricted",
      switchableError: false,
      errorExplanation: "请求发起端国家未知(UNKNOWN)，所有声明合规限制的供应商被全部硬性拦截，无法调度任何来源",
      decisionChain: [
        { stage: "合规硬过滤", detail: "请求国 UNKNOWN 触发安全 Fail-Closed，全部受限来源被排除", status: "excluded" },
        { stage: "终结决策", detail: "无合法可用来源，直接返回 451 geo_restricted，不发生跨模型回退", status: "failed" },
      ],
    },
  ],
  demo: true,
};

async function get<T>(path: string): Promise<T> {
  return request<T>(path);
}

export async function loadAdminData(): Promise<AdminData> {
  if (import.meta.env.DEV && import.meta.env.VITE_ADMIN_DEMO === "true") return structuredClone(demoData);
    const [dashboard, models, sources, routes, geoPolicies, opportunities, keys, priority, audits] = await Promise.all([
      get<DashboardData>("/dashboard"),
      get<ModelRow[]>("/models"),
      get<SourceRow[]>("/sources"),
      get<RouteRow[]>("/routes"),
      get<GeoPolicyRow[]>("/geo-policies"),
      get<OpportunityRow[]>("/opportunities"),
      get<KeyRow[]>("/keys"),
      get<CountryPriorityRow>("/country-priorities/global"),
      get<AuditRow[]>("/audits"),
    ]);
    return {
      dashboard,
      models,
      sources,
      routes,
      geoPolicies,
      opportunities,
      keys,
      priority,
      audits,
      budget: await get<BudgetStatus>("/budget"),
      recentLogs: [],
      demo: false,
    };
}

export async function loadOpenCodeConfig(): Promise<Record<string, unknown>> {
  return get<Record<string, unknown>>("/opencode-config");
}

export async function mutate<T>(path: string, method: "POST" | "PUT" | "DELETE", body?: unknown): Promise<T> {
  const init: RequestInit = {
    method,
    headers: { "content-type": "application/json", accept: "application/json" },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  return request<T>(path, init);
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers: { accept: "application/json", ...init.headers },
      signal: AbortSignal.timeout(20_000),
    });
  } catch (error) {
    if (error instanceof Error && ["TimeoutError", "AbortError"].includes(error.name)) {
      throw new Error("请求超时，请刷新确认保存结果后再重试");
    }
    throw new Error("无法连接管理 API，请检查网络后重试");
  }
  const value = await response.json().catch(() => null) as (T & { error?: string }) | null;
  if (!response.ok) throw new Error(value?.error ?? `管理 API 返回 HTTP ${response.status}，请检查 Pages Functions 与服务端配置`);
  if (value === null) throw new Error("管理 API 未返回 JSON，请检查 Pages Functions 部署或重新登录 Cloudflare Access");
  return value;
}
